import { useQueries } from '@tanstack/react-query';

import { MappedInternalPropertyWithOffers, listMappedInternalProperties } from '~/services/AccommodationService';
import ReservationService from '~/services/ReservationService';

export interface RatePlanOption {
  id: string;
  name: string;
  status?: string;
}

interface ReservationRatePlanListItem {
  id: string;
  name: string;
  status?: string;
}

interface ReservationListResponse {
  result?: Array<ReservationRatePlanListItem>;
}

const RESERVATION_SOURCE = 'reservation';

export interface UseBundleRatePlanOptionsResult {
  ratePlansById: Map<string, RatePlanOption>;
  vendorIdByRatePlanId: Map<string, string>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Resolves the set of LPC rate plans available across a set of svc-accommodation properties.
 *
 * Three-step fan-out:
 *   1. For each property → `listMappedInternalProperties` to find its reservation-source mapping.
 *   2. For each reservation property → `ReservationService.getProperty` to read the vendor's
 *      Salesforce id (the rate-plans endpoint uses Salesforce ids, not the Siteminder vendor code).
 *   3. For each vendor → `ReservationService.getRatePlans` and aggregate/dedupe by id.
 *
 * Each step uses `combine` to produce a referentially stable result so callers can use the value
 * directly in `useMemo` deps without re-running every render (useQueries returns a fresh array
 * identity each render in v5).
 */
export function useBundleRatePlanOptions(propertyIds: Array<string>, enabled: boolean): UseBundleRatePlanOptionsResult {
  const gated = enabled && propertyIds.length > 0;

  const mapped = useQueries({
    queries: propertyIds.map((propertyId) => ({
      queryKey: ['bundle-lpc-mapped-internal-properties', propertyId],
      enabled: gated,
      staleTime: 5 * 60_000,
      retry: 1,
      queryFn: async (): Promise<Array<MappedInternalPropertyWithOffers>> => {
        const res = await listMappedInternalProperties({
          propertyId,
          verificationStatuses: [],
        });
        return res?.result ?? [];
      },
    })),
    combine: (results) => {
      const ids = new Set<string>();
      for (const r of results) {
        for (const entry of r.data ?? []) {
          if (entry.internalProperty?.source !== RESERVATION_SOURCE) continue;
          // `internalProperty.sourceId` is the svc-reservation property UUID.
          const reservationPropertyId = entry.internalProperty.sourceId;
          if (reservationPropertyId) ids.add(reservationPropertyId);
        }
      }
      return {
        reservationPropertyIds: [...ids].sort(),
        isFetching: results.some((r) => r.isFetching),
        isError: results.some((r) => r.isError),
      };
    },
  });

  const reservationProperties = useQueries({
    queries: mapped.reservationPropertyIds.map((reservationPropertyId) => ({
      queryKey: ['bundle-lpc-reservation-property', reservationPropertyId],
      enabled: gated,
      staleTime: 5 * 60_000,
      retry: 1,
      queryFn: async () => {
        const res = (await ReservationService.getProperty(reservationPropertyId)) as {
          result?: Record<string, unknown>;
        };
        return res?.result ?? null;
      },
    })),
    combine: (results) => {
      const ids = new Set<string>();
      for (const r of results) {
        const prop = r.data as
          | {
              vendor?: { id_salesforce_external?: string };
              id_salesforce_external?: string;
              vendor_id_salesforce_external?: string;
            }
          | null
          | undefined;
        if (!prop) continue;
        const sfId =
          prop.vendor?.id_salesforce_external ?? prop.vendor_id_salesforce_external ?? prop.id_salesforce_external;
        if (sfId) ids.add(sfId);
      }
      return {
        vendorIds: [...ids].sort(),
        isFetching: results.some((r) => r.isFetching),
        isError: results.some((r) => r.isError),
      };
    },
  });

  const ratePlans = useQueries({
    queries: reservationProperties.vendorIds.map((vendorId) => ({
      queryKey: ['bundle-lpc-rate-plans-by-vendor', vendorId],
      enabled: gated,
      staleTime: 60_000,
      retry: 1,
      queryFn: async () => {
        const res = (await ReservationService.getRatePlans(vendorId)) as ReservationListResponse;
        return { vendorId, plans: res?.result ?? [] };
      },
    })),
    combine: (results) => {
      // Only surface `content-approved` rate plans. Bundles must only be associated with rate
      // plans that have completed content approval, so we never let admins pick non-approved ones
      // from the dropdown (and never auto-save them on edit either).
      const byId = new Map<string, RatePlanOption>();
      const vendorByPlanId = new Map<string, string>();
      for (const r of results) {
        const payload = r.data;
        if (!payload) continue;
        for (const p of payload.plans) {
          if (p.status !== 'content-approved') continue;
          if (!byId.has(p.id)) byId.set(p.id, { id: p.id, name: p.name, status: p.status });
          if (!vendorByPlanId.has(p.id)) vendorByPlanId.set(p.id, payload.vendorId);
        }
      }
      return {
        ratePlansById: byId,
        vendorIdByRatePlanId: vendorByPlanId,
        isFetching: results.some((r) => r.isFetching),
        isError: results.some((r) => r.isError),
      };
    },
  });

  return {
    ratePlansById: ratePlans.ratePlansById,
    vendorIdByRatePlanId: ratePlans.vendorIdByRatePlanId,
    isLoading: mapped.isFetching || reservationProperties.isFetching || ratePlans.isFetching,
    isError: mapped.isError || reservationProperties.isError || ratePlans.isError,
  };
}
